// @ts-nocheck
import { runInAction, makeAutoObservable, reaction, computed } from "mobx";
import { utils, uuid } from "@intrasync/utils";
// Uses the local watchComputed — see the note in RouterStore.ts: the library's
// /mobx subpath does not resolve under "moduleResolution": "Node" and its
// signature is stricter than this call site allows.
import { watchComputed } from "shared/common/mobx";

export default class RequestQueueStore {
  rootStore;
  options = {};

  requestQueue = [
    /**
     * This new feature will allow debouncing of server requests.
     * The goal is that the request queue processor prioritizes UI reflecting requests
     * A nice to have would be the ability to cancel requests in the queue when leaving the page that requested them
     * requestQueueItem: {
     *    requestPriority: 1-3, // With 1 being UI required requests, 2(default) being table filling, and 3 would be considered lazy
     *    rootStore[nextRequest.requestStore]: 'panelStore', // Store name
     *    requestMethod: 'find', // The method to be executed
     *    requestId: 2 || 'asd9jk123d89n9', // **Optional, request id if referencing a single item
     *    requestData: {dbKey: 'dbData'}, // **Optional, request data if sending create/patch
     *    requestParams: {query: {jobNumber: 100}}, // **Optional, request params, including query
     *    cancelRequestBeforeExit: 'Home' // **Optional, your route name that needed the data, so if the user leaves and it hasn't been completed it can get removed
     * }
     */
  ];

  constructor(rootStore, options) {
    this.rootStore = rootStore || null;
    this.debugMode = options.debugMode || false;
    this.queueEnabled = options.queueEnabled || false;

    makeAutoObservable(this, {
      rootStore: false,
    });

    if (this.queueEnabled) {
      this.requestQueueProcessorDispose = watchComputed(
        this,
        "requestQueueProcessor"
      );
      this.requestQueueDispose = reaction(
        () => this.requestQueueProcessor,
        async (currentQueueItems) => {
          if (currentQueueItems?.length > 0) {
            console.log("Processing Next Request");
            const nextRequest = currentQueueItems[0];
            console.log("Request", nextRequest);
            if (
              nextRequest.requestId &&
              nextRequest.requestData &&
              nextRequest.requestParams
            ) {
              // Only patch and update will ever have all 3
              if (nextRequest.requestMethod === "patch") {
                return rootStore[nextRequest.requestStore]
                  .patch(
                    nextRequest.requestId,
                    nextRequest.requestData,
                    nextRequest.requestParams
                  )
                  .then(function () {
                    runInAction(() => this.updateQueue(nextRequest));
                  });
              }
              if (nextRequest.requestMethod === "update") {
                return rootStore[nextRequest.requestStore]
                  .update(
                    nextRequest.requestId,
                    nextRequest.requestData,
                    nextRequest.requestParams
                  )
                  .then(function () {
                    runInAction(() => this.updateQueue(nextRequest));
                  });
              }
            }
            if (
              nextRequest.requestId &&
              nextRequest.requestData &&
              !nextRequest.requestParams
            ) {
              // Only patch and update will ever have an id and data
              if (nextRequest.requestMethod === "patch") {
                return rootStore[nextRequest.requestStore]
                  .patch(nextRequest.requestId, nextRequest.requestData)
                  .then(function () {
                    runInAction(() => this.updateQueue(nextRequest));
                  });
              }
              if (nextRequest.requestMethod === "update") {
                return rootStore[nextRequest.requestStore]
                  .update(nextRequest.requestId, nextRequest.requestData)
                  .then(function () {
                    runInAction(() => this.updateQueue(nextRequest));
                  });
              }
            }

            if (
              nextRequest.requestId &&
              !nextRequest.requestData &&
              nextRequest.requestParams
            ) {
              // Only get and remove will ever have just id and params
              if (nextRequest.requestMethod === "get") {
                return rootStore[nextRequest.requestStore]
                  .get(nextRequest.requestId, nextRequest.requestParams)
                  .then(function () {
                    runInAction(() => this.updateQueue(nextRequest));
                  });
              }
              if (nextRequest.requestMethod === "remove") {
                return rootStore[nextRequest.requestStore]
                  .remove(nextRequest.requestId, nextRequest.requestParams)
                  .then(function () {
                    runInAction(() => this.updateQueue(nextRequest));
                  });
              }
            }

            if (
              !nextRequest.requestId &&
              nextRequest.requestData &&
              nextRequest.requestParams
            ) {
              if (nextRequest.requestMethod === "create") {
                return rootStore[nextRequest.requestStore]
                  .create(nextRequest.requestData, nextRequest.requestParams)
                  .then(function () {
                    runInAction(() => this.updateQueue(nextRequest));
                  });
              }
              if (nextRequest.requestMethod === "patch") {
                return rootStore[nextRequest.requestStore]
                  .patch(
                    null,
                    nextRequest.requestData,
                    nextRequest.requestParams
                  )
                  .then(function () {
                    runInAction(() => this.updateQueue(nextRequest));
                  });
              }
              if (nextRequest.requestMethod === "update") {
                return rootStore[nextRequest.requestStore]
                  .update(
                    null,
                    nextRequest.requestData,
                    nextRequest.requestParams
                  )
                  .then(function () {
                    runInAction(() => this.updateQueue(nextRequest));
                  });
              }
            }

            if (
              !nextRequest.requestId &&
              !nextRequest.requestData &&
              nextRequest.requestParams
            ) {
              if (nextRequest.requestMethod === "find") {
                return rootStore[nextRequest.requestStore]
                  .find(nextRequest.requestParams)
                  .then(function () {
                    runInAction(() => this.updateQueue(nextRequest));
                  });
              }
              if (nextRequest.requestMethod === "get") {
                return rootStore[nextRequest.requestStore]
                  .get(null, nextRequest.requestParams)
                  .then(function () {
                    runInAction(() => this.updateQueue(nextRequest));
                  });
              }
              if (nextRequest.requestMethod === "remove") {
                return rootStore[nextRequest.requestStore]
                  .remove(null, nextRequest.requestParams)
                  .then(function () {
                    runInAction(() => this.updateQueue(nextRequest));
                  });
              }
            }

            if (
              !nextRequest.requestId &&
              nextRequest.requestData &&
              !nextRequest.requestParams
            ) {
              if (nextRequest.requestMethod === "create") {
                return rootStore[nextRequest.requestStore]
                  .create(nextRequest.requestData)
                  .then(function () {
                    runInAction(() => this.updateQueue(nextRequest));
                  });
              }
            }

            if (
              nextRequest.requestId &&
              !nextRequest.requestData &&
              !nextRequest.requestParams
            ) {
              if (nextRequest.requestMethod === "get") {
                return rootStore[nextRequest.requestStore]
                  .get(nextRequest.requestId)
                  .then(function () {
                    runInAction(() => this.updateQueue(nextRequest));
                  });
              }
              if (nextRequest.requestMethod === "remove") {
                return rootStore[nextRequest.requestStore]
                  .remove(nextRequest.requestId)
                  .then(function () {
                    runInAction(() => this.updateQueue(nextRequest));
                  });
              }
            }

            console.warn(
              "If we see this message we used the queue system incorrectly",
              nextRequest
            );
            return runInAction(() => this.updateQueue(nextRequest));
          }
        },
        { fireImmediately: true, delay: 250 }
      );
    }
  }

  dispose = () => {
    this.requestQueueDispose && this.requestQueueDispose();
    this.requestQueueProcessorDispose && this.requestQueueProcessorDispose();
  };

  handleDebugMode = () => {
    this.debugMode = !this.debugMode;
  };

  updateQueue = (finishedRequest) => {
    if (!finishedRequest) {
      throw Error("Requires a request to be removed from the queue");
    }
    const sortedRequestQueue = this.requestQueue
      .slice()
      .filter((request) => request.uuid !== finishedRequest.uuid)
      .sort((a, b) => a.requestPriority - b.requestPriority);
    console.log("updateQueue Removed Request", finishedRequest);
    this.requestQueue.replace(sortedRequestQueue);
  };

  addRequest = ({
    requestPriority = 2, // With 1 being UI required requests, 2(default) being table filling, and 3 would be considered lazy
    requestStore,
    requestMethod,
    requestId,
    requestData,
    requestParams,
    cancelRequestBeforeExit,
  }) => {
    if (!requestStore) {
      throw Error("Requires a service to send the request to");
    }
    if (!requestMethod) {
      throw Error("Requires a service method to send the request to");
    }

    const sortedRequestQueue = [
      ...this.requestQueue.slice(),
      {
        requestPriority,
        requestStore,
        requestMethod,
        requestId,
        requestData,
        requestParams,
        cancelRequestBeforeExit,
        uuid: uuid(),
      },
    ].sort((a, b) => a.requestPriority - b.requestPriority);

    this.requestQueue.replace(sortedRequestQueue);
    console.log("updateQueue Added Request", {
      requestPriority,
      requestStore,
      requestMethod,
      requestId,
      requestData,
      requestParams,
      cancelRequestBeforeExit,
      uuid: uuid(),
    });
  };

  cancelRequests = (routeName) => {
    if (!routeName) {
      throw Error("Requires a routeName to cancel requests for");
    }

    const sortedRequestQueue = [
      ...this.requestQueue.filter(
        (request) =>
          !request.cancelRequestBeforeExit ||
          request.cancelRequestBeforeExit !== routeName
      ),
    ].sort((a, b) => a.requestPriority - b.requestPriority);

    this.requestQueue.replace(sortedRequestQueue);
    console.log("updateQueue Cancelled All Requests For", routeName);
  };

  get requestQueueProcessor() {
    if (!this.requestQueue || this.requestQueue.length === 0) return [];
    return [
      ...this.requestQueue.map((request) => {
        return { ...request };
      }),
    ];
  }

  get requestQueueLength() {
    console.log("updateQueue Length", this.requestQueueProcessor.length);
    return this.requestQueueProcessor.length || 0;
  }

  get storesWithRequests() {
    if (!this.requestQueueProcessor || this.requestQueueProcessor.length === 0)
      return [];
    return this.requestQueueProcessor.map((request) => request.requestStore);
  }
}
